import React, {
    useCallback,
    useRef,
    useEffect,
    MutableRefObject,
} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useMeasure} from 'react-use';
import {UseMeasureRef} from 'react-use/lib/useMeasure';
import {ApTooltip} from '@accurate-player/accurate-player-components-external';
import {ifApTooltip} from '@pluto/player-check/player-check';

import Tooltip from './Tooltip';

export interface TooltipContainerProps {
    headerText: string;
    text: string;
    targetTop: number;
    targetLeft: number;
    targetWidth: number;
    maxRight: number;
}

type TooltipOrNull = ApTooltip | null;
type TooltipRef = MutableRefObject<TooltipOrNull>;
type ComponentRefs = {
    tooltipRef: TooltipRef,
    measureRef: UseMeasureRef<HTMLElement>,
}

export const setTooltipPosition = (props: TooltipContainerProps, node: ApTooltip) => {
    const {
        targetLeft,
        targetTop,
        targetWidth,
        maxRight,
    } = props;
    const marginBottom = 5;
    const {
        width,
        height,
    } = node.getBoundingClientRect();
    
    const maxLeft = maxRight - width;
    const tooltipLeft = targetLeft + (targetWidth / 2) - (width / 2);
    const left = Math.min(maxLeft, tooltipLeft);
    
    node.style.position = 'fixed';
    node.style.visibility = 'visible';
    node.style.opacity = '1';
    node.style.left = `${left}px`;
    node.style.top = `${targetTop - height - marginBottom}px`;
};

export const updateTooltipPosition = wrap(ifApTooltip((props: TooltipContainerProps, node: ApTooltip) => {
    setTooltipPosition(props, node);
}));

export const handleTooltipChange = curry((refs: ComponentRefs, node: ApTooltip) => {
    const {
        tooltipRef,
        measureRef,
    } = refs;
    
    tooltipRef.current = node;
    measureRef(node);
});

const useTooltipRef = (refs: ComponentRefs) => {
    const onTooltipChange = handleTooltipChange(refs);
    const setRef = useCallback(onTooltipChange, []);
    
    return [setRef];
};

export default function TooltipContainer(props: TooltipContainerProps) {
    const {
        headerText,
        text,
    } = props;
    const tooltipRef = useRef<TooltipOrNull>(null);
    const [measureRef, {height}] = useMeasure();
    const [setTooltipRef] = useTooltipRef({
        tooltipRef,
        measureRef,
    });
    const handleUpdatePosition = updateTooltipPosition(tooltipRef.current, props, tooltipRef.current);
    
    useEffect(handleUpdatePosition, [props, tooltipRef, height]);
    
    return (
        <Tooltip
            setTooltipRef={setTooltipRef}
            headerText={headerText}
            text={text}
        />
    );
}
